import amaLog from '../helpers/log';
import { getTopOffset } from './custom_no';

interface FullscreenData {
  video_url?: string;
  single_frame?: number;
  click_url?: string;
  background_portrait?: string;
  background_landscape?: string;
  eventId?: string;
  threshold?: number;
  [key: string]: any;
}

const ua = window.navigator.userAgent.toLowerCase();
let fullscreens: HTMLElement[] = [];
const threshold = Math.max(document.documentElement.clientHeight, window.innerHeight || 0) * 1.5;
const buffer = window.innerWidth <= 640 ? 400 : 0;

const update = () => {
  const activeFullScreen = findActiveFullscreen();
  setAndRemoveActiveStateOnFullscreen(activeFullScreen);
};

const findActiveFullscreen = (): HTMLElement | null => {
  const scrollTop = window.pageYOffset !== undefined ? window.pageYOffset : document.body.scrollTop;
  let activeFullScreen: HTMLElement | null = null;
  let pxFromFullscreen: number | null = null;
  for (let i = 0; i < fullscreens.length; i++) {
    const fullscreen = fullscreens[i];
    const adTopOffset = parseInt(fullscreen.getAttribute('data-top-offset') || '0', 10);
    const top = adTopOffset - threshold;
    const bottom =
      parseInt(fullscreen.getAttribute('data-top-offset') || '0', 10) +
      parseInt(fullscreen.getAttribute('data-offsetheight') || '0', 10);

    const topDiff = scrollTop - bottom;
    const bottomDiff = scrollTop + (window.innerHeight || 0) - adTopOffset;
    const currentPxFromFullscreen = Math.min(Math.abs(topDiff), Math.abs(bottomDiff));

    if (scrollTop > top - buffer && scrollTop < bottom + buffer) {
      if (!activeFullScreen || (pxFromFullscreen !== null && pxFromFullscreen > currentPxFromFullscreen)) {
        pxFromFullscreen = currentPxFromFullscreen;
        activeFullScreen = fullscreen;
      }
    }
  }
  return activeFullScreen;
};

const setAndRemoveActiveStateOnFullscreen = (activeFullScreen: HTMLElement | null) => {
  for (let i = 0; i < fullscreens.length; i++) {
    const fullscreen = fullscreens[i];
    if (fullscreen === activeFullScreen) {
      addActiveStateToFullscreen(fullscreen);
    } else {
      removeActiveStateFromFullscreen(fullscreen);
    }
  }
};

const addActiveStateToFullscreen = (fullscreen: HTMLElement) => {
  if (fullscreen.className && fullscreen.className.indexOf('active') === -1) {
    fullscreen.setAttribute('data-top-offset', String(getTopOffset(fullscreen)));
    fullscreen.className += ' active';
  }
};

const removeActiveStateFromFullscreen = (fullscreen: HTMLElement) => {
  if (fullscreen.className && fullscreen.className.indexOf('active') > -1) {
    fullscreen.className = fullscreen.className.replace(new RegExp('(^|\\b)' + 'active' + '(\\b|$)', 'gi'), ' ');
  }
};

const throttle = (fn: (...args: any[]) => void, threshhold?: number, scope?: any) => {
  threshhold = threshhold || 250;
  let last: number | undefined, deferTimer: number | undefined;
  return function (this: any, ...args: any[]) {
    const context = scope || this;
    const now = +new Date();
    if (last && now < last + threshhold) {
      clearTimeout(deferTimer);
      deferTimer = window.setTimeout(function () {
        last = now;
        fn.apply(context, args);
      }, threshhold);
    } else {
      last = now;
      fn.apply(context, args);
    }
  };
};

const findFullscreens = () => {
  fullscreens = Array.prototype.filter.call(document.querySelectorAll('.adunit'), function (el: HTMLElement) {
    if (ua.indexOf('iphone') > -1) {
      if (el.className.match(/small-fullscreen/)) {
        if (window.innerWidth > window.innerHeight) {
          el.className += ' medium-fullscreen-x medium-viewport';
          return el;
        }
      }
    }

    if (window.innerWidth <= 640) {
      return el.className.match(/small-fullscreen/);
    } else if (window.innerWidth < 1024) {
      return el.className.match(/medium-fullscreen/);
    } else {
      return el.className.match(/large-fullscreen/);
    }
  });

  if (fullscreens.length) {
    for (let i = 0; i < fullscreens.length; i++) {
      //   if (window.am.libs.adsDefer.headerIsSticky) {
      //     fullscreens[i].classList.add('headerIsSticky');
      //   }
      if (ua.indexOf('_app_') > -1 && (ua.indexOf('iphone') > -1 || ua.indexOf('ipad') > -1)) {
        fullscreens[i].className += ' ios-app-style';
      }
      if (
        navigator.userAgent.indexOf('MSIE') !== -1 ||
        navigator.appVersion.indexOf('Trident/') > 0 ||
        navigator.userAgent.indexOf('Edge/') > 0
      ) {
        fullscreens[i].className += ' legacy';
      }
      if (fullscreens[i].parentElement) {
        (fullscreens[i].parentElement as HTMLElement).style.padding = '0px';
      }
    }
  }
};

const doubleFullscreen = (data: FullscreenData, fs: HTMLElement | undefined) => {
  if (!fs || !data) {
    return;
  }
  fs.classList.add('legacy');
  fs.classList.add('double');
  document.body.classList.add('has-doublefullscreen');
  if (data.video_url && data.video_url.indexOf('https') === 0) {
    const frame = document.createElement('iframe');
    frame.setAttribute('frameborder', String(0));
    frame.setAttribute('scrolling', 'no');
    frame.setAttribute('src', data.video_url);
    frame.setAttribute('class', 'double-video-frame');

    if (data.single_frame && data.single_frame == 1) {
      frame.setAttribute('style', 'z-index:1;');
    }

    if (data.click_url && data.click_url.indexOf('https') === 0) {
      frame.setAttribute('name', data.click_url);
    }

    fs.insertAdjacentElement('afterbegin', frame);
  } else if (data.background_portrait && data.background_landscape) {
    if (window.matchMedia('(orientation: portrait)').matches) {
      fs.style.backgroundImage = `url(${data.background_portrait})`;
    } else {
      fs.style.backgroundImage = `url(${data.background_landscape})`;
    }
  }
};

const tripleFullscreen = (data: FullscreenData, fs: HTMLElement | undefined) => {
  if (!fs || !data) {
    return;
  }
  fs.classList.add('legacy');
  fs.classList.add('triple');
  document.body.classList.add('has-triplefullscreen');
  if (data.video_url && data.video_url.indexOf('https') === 0) {
    const frame = document.createElement('iframe');
    frame.setAttribute('frameborder', String(0));
    frame.setAttribute('scrolling', 'no');
    frame.setAttribute('src', data.video_url);
    frame.setAttribute('class', 'triple-video-frame');

    if (data.single_frame && data.single_frame == 1) {
      frame.setAttribute('style', 'z-index:1;');
    }

    if (data.click_url && data.click_url.indexOf('https') === 0) {
      frame.setAttribute('name', data.click_url);
    }

    fs.insertAdjacentElement('afterbegin', frame);
  } else if (data.background_portrait && data.background_landscape) {
    if (window.matchMedia('(orientation: portrait)').matches) {
      fs.style.backgroundImage = `url(${data.background_portrait})`;
    } else {
      fs.style.backgroundImage = `url(${data.background_landscape})`;
    }
  }
};

const createWaypoint = (
  source: Window | MessagePort | ServiceWorker,
  data: FullscreenData,
  fs: HTMLElement | undefined,
) => {
  if (!fs || !source || !data) {
    return;
  }
  function createObserver(el: HTMLElement, th: number) {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: th,
    };
    const observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(el);
  }

  function handleIntersect(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      source.postMessage(
        JSON.stringify({
          callbackId: (entry.target as HTMLElement).dataset.callbackid,
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          isVisible: (entry as any).isVisible,
        }),
        '*' as any,
      );
    });
  }
  const newWP = document.createElement('span');
  newWP.classList.add('waypoint');
  newWP.dataset.callbackid = data.eventId;
  newWP.style.top =
    data.threshold && data.threshold <= 1 ? `${Math.round(data.threshold * 100)}%` : `${data.threshold}%`;
  fs.appendChild(newWP);
  createObserver(newWP, 1);
};

const checkValidOrigin = (origin: string): boolean => {
  const validOrigins = [
    'https://www.dagbladet.no',
    'https://www.seher.no',
    'https://www.elbil24.no',
    'https://www.kk.no',
    'https://www.vi.no',
    'https://sol.no',
    'https://dinside.dagbladet.no',
    'https://borsen.dagbladet.no',
    'https://tpc.googlesyndication.com',
    'https://securepubads.g.doubleclick.net',
    'https://delivered-by-madington.com',
    'https://adtech.aller.no',
  ];

  if (origin.includes('safeframe.googlesyndication.com')) {
    return true;
  }

  return validOrigins.some(function (originVal) {
    return origin === originVal;
  });
};

const onMessage = (event: MessageEvent) => {
  try {
    if (!checkValidOrigin(event.origin)) {
      throw 'Unsafe';
    }

    const payload = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
    if (payload.callback === 'am.libs.adsFullscreen.doubleFullscreen') {
      amaLog('Madington running double fullscreen');
      doubleFullscreen(payload, fullscreens[0]);
    }
    if (payload.callback === 'am.libs.adsFullscreen.tripleFullscreen') {
      amaLog('Madington running triple fullscreen');
      tripleFullscreen(payload, fullscreens[0]);
    }
    if (payload.callback === 'am.libs.adsFullscreen.addWaypoint' || payload.action === 'addWaypoint') {
      createWaypoint(event.source, payload, fullscreens[0]);
    }
  } catch (e) {
    return false;
  }
};

const init = () => {
  findFullscreens();
  if (fullscreens.length) {
    window.addEventListener(
      'scroll',
      throttle(function () {
        update();
      }, 25),
    );
    window.addEventListener(
      'resize',
      throttle(function () {
        update();
      }, 100),
    );
    window.addEventListener('message', onMessage);
  }

  update();
};

const reflow = findFullscreens;

export const adsFullscreen = {
  init,
  reflow,
};
